import { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Serie } from '@rossum/api-client/annotations';
import { useTheme } from '@rossum/ui/material';
import { BarChart } from '@rossum/ui/x-charts-pro';
import { toHumanString } from '../../../../lib/helpers';
import { dateFormatByGranularity } from '../../../../lib/timeUtils';
import EmptySeries from '../../components/EmptySeries';
import GraphContainer from '../../components/GraphContainer';
import { GranularityT } from '../Granularity';

const BAR_CHART_HEIGHT = 280;

type Props = {
  isTmpState: boolean;
  children?: ReactNode;
  series: Array<Serie>;
  groupBy: GranularityT;
};

const RejectionGraph = ({ isTmpState, series, children, groupBy }: Props) => {
  const intl = useIntl();
  const theme = useTheme();

  const data = useMemo(
    () =>
      series.map(
        ({
          values: { rejectedAutomaticallyCount, rejectedManuallyCount },
          beginDate,
          endDate,
        }) => ({
          rejectedManuallyCount: rejectedManuallyCount ?? 0,
          rejectedAutomaticallyCount: rejectedAutomaticallyCount ?? 0,
          date: dateFormatByGranularity(
            intl.locale,
            groupBy,
            beginDate,
            endDate
          ),
        })
      ),
    [series, intl.locale, groupBy]
  );

  const displayPlaceholder = data.every(
    d => d.rejectedManuallyCount === 0 && d.rejectedAutomaticallyCount === 0
  );

  return (
    <GraphContainer graphName="rejection">
      <span style={{ opacity: displayPlaceholder ? 0.1 : 1 }}>{children}</span>
      {displayPlaceholder ? (
        <EmptySeries inGraph graphName="rejection" />
      ) : (
        <BarChart
          skipAnimation
          height={BAR_CHART_HEIGHT}
          series={[
            {
              data: data.map(d => d.rejectedManuallyCount),
              stack: 'total',
              color: isTmpState
                ? theme.palette.text.disabled
                : theme.palette.primary.main,
              label: isTmpState
                ? undefined
                : intl.formatMessage({
                    id: 'components.graphs.rejection.series.rejectedManuallyCount',
                  }),
            },
            {
              data: data.map(d => d.rejectedAutomaticallyCount),
              stack: 'total',
              color: isTmpState
                ? theme.palette.grey[700]
                : theme.palette.success.main,
              label: isTmpState
                ? undefined
                : intl.formatMessage({
                    id: 'components.graphs.rejection.series.rejectedAutomaticallyCount',
                  }),
            },
          ]}
          xAxis={[
            {
              data: data.map((_, i) => i),
              scaleType: 'band',
              valueFormatter: (v: number) => data[v]?.date ?? '',
            },
          ]}
          yAxis={[{ min: 0, tickNumber: 4, valueFormatter: toHumanString }]}
          grid={{ horizontal: true }}
          hideLegend
        />
      )}
    </GraphContainer>
  );
};

export default RejectionGraph;
